import { toastContent } from 'config/toast';
import { ErrorStatusType } from 'types/StatusTypes';

const TRANSLATOR_DOM_ERROR_PATTERN =
  /insertBefore|removeChild|not a child of this node|Failed to execute '.+' on 'Node'/i;

export type UserFacingError = {
  title: string;
  description: string;
};

const defaultError = toastContent.error[ErrorStatusType.DEFAULT];
const serverError = toastContent.error[ErrorStatusType.SERVER_ERROR];

export const connectionError: UserFacingError = {
  title: 'Verbindungsproblem',
  description: 'Bitte überprüfe deine Internetverbindung und versuche es erneut.',
};

export const notFoundError: UserFacingError = {
  title: 'Inhalt nicht gefunden',
  description: 'Der angeforderte Inhalt existiert nicht oder wurde entfernt.',
};

export const forbiddenError: UserFacingError = {
  title: 'Kein Zugriff',
  description: 'Du hast keinen Zugriff auf diesen Inhalt.',
};

export const unauthorizedError: UserFacingError = {
  title: 'Anmeldung erforderlich',
  description: 'Bitte melde dich an, um fortzufahren.',
};

export const runtimeError: UserFacingError = {
  title: defaultError.title ?? 'Hier ist etwas schiefgelaufen.',
  description:
    defaultError.description ??
    'Falls dieser Fehler häufiger auftritt, wende dich bitte an unseren Support 🙂',
};

export function isBrowserTranslatorActive(): boolean {
  const { classList } = document.documentElement;
  return classList.contains('translated-ltr') || classList.contains('translated-rtl');
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (typeof error === 'string') {
    return error;
  }
  return '';
}

export function isTranslatorRelatedError(error: unknown): boolean {
  return TRANSLATOR_DOM_ERROR_PATTERN.test(getErrorMessage(error));
}

export function resolveTranslatorUserMessage(): UserFacingError {
  return {
    title: 'Übersetzer aktiv',
    description:
      'Manchmal verursacht der Übersetzer Probleme mit der Nutzung von Novaheal. Bitte schalte den Browser-Übersetzer aus, um Novaheal im vollen Umfang zu nutzen.',
  };
}

export function resolveRuntimeUserMessage(): UserFacingError {
  return runtimeError;
}

export function resolveAxiosUserMessage(status: number | undefined): UserFacingError {
  if (status === undefined) {
    return connectionError;
  }

  switch (status) {
    case 404:
      return notFoundError;
    case 403:
      return forbiddenError;
    case 401:
      return unauthorizedError;
    case 408:
    case 500:
    case 502:
    case 503:
      return {
        title: serverError.title ?? 'Technischer Fehler',
        description:
          serverError.description ??
          'Falls dieser Fehler häufiger auftritt, wende dich bitte an unseren Support 🙂',
      };
    default:
      return {
        title: defaultError.title ?? 'Hier ist etwas schiefgelaufen.',
        description:
          defaultError.description ??
          'Falls dieser Fehler häufiger auftritt, wende dich bitte an unseren Support 🙂',
      };
  }
}
